import React, { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { useSelector } from "react-redux";
import { axiosNewInstance } from "../../config/Api";
import LangData from "../common/LangData";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

const formatDate = (date) => {
    if (!date) return "";
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
};

const AgencyDetails = () => {
    const history = useHistory();
    const token = localStorage.getItem("authToken");
    const mobile = useSelector((state) => state?.user?.loginuser?.mobile) || "";
    const roleId = useSelector((state) => state?.user?.loginuser?.role_id);
    const isAgent = roleId === 4;
    const isLicEmployee = roleId === 2;

    // Date filter state — default to last 3 months
    const today = new Date();
    const fifteenDaysAgo = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 15);
    const [startDt, setStartDt] = useState(fifteenDaysAgo);
    const [endDt, setEndDt] = useState(today);

    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);
    const [error, setError] = useState(null);
    const [agencyCode, setAgencyCode] = useState(null);
    const [agentInfo, setAgentInfo] = useState(null);
    const [agencyLoading, setAgencyLoading] = useState(isAgent);
    // LIC Employee: manual agent code input
    const [manualAgentCode, setManualAgentCode] = useState("");

    // Step 1: For Agents — fetch agency code and agent info on mount
    useEffect(() => {
        if (!isAgent) return;
        if (!token) {
            setAgencyLoading(false);
            history.push("/login");
            return;
        }
        const mobileNo = (mobile || "").toString().trim().replace(/\D/g, "");
        if (!mobileNo || mobileNo.length < 10) {
            setError("Mobile number not found or invalid. Please add a valid mobile in your profile.");
            setAgencyLoading(false);
            return;
        }

        let cancelled = false;
        const fetchAgencyCode = async () => {
            try {
                const agentRes = await axiosNewInstance.get(
                    `api/agentEnquiryLogin/${encodeURIComponent(mobileNo)}`,
                    {
                        headers: {
                            Authorization: `Bearer ${token}`,
                            Accept: "application/json",
                        },
                    }
                );
                const agentData = agentRes?.data;
                if (cancelled) return;
                if (agentData?.Error) {
                    setError(agentData.Error);
                    return;
                }
                const code = agentData?.agencyCode || "";
                if (!code) {
                    setError("Agency code not found from agent details.");
                    return;
                }
                setAgencyCode(code);
                setAgentInfo(agentData);
            } catch (err) {
                if (cancelled) return;
                const message =
                    err?.response?.data?.Error ||
                    err?.response?.data?.message ||
                    err?.message ||
                    "Unable to fetch agent details.";
                setError(message);
            } finally {
                if (!cancelled) setAgencyLoading(false);
            }
        };

        fetchAgencyCode();
        return () => { cancelled = true; };
    }, [token, mobile, history, isAgent]);

    // Step 2: For Agents — auto-fetch agency details when agencyCode is available
    useEffect(() => {
        if (isAgent && agencyCode && startDt && endDt) {
            fetchAgencyDetails();
        }
    }, [agencyCode]);

    // Fetch agency details (called on search or auto-fetch)
    const fetchAgencyDetails = async () => {
        const codeToUse = isLicEmployee ? manualAgentCode.trim() : agencyCode;
        if (!codeToUse) {
            setError("Please enter an Agent Code.");
            return;
        }
        if (!startDt || !endDt) {
            setError("Please select both Start Date and End Date.");
            return;
        }

        setLoading(true);
        setError(null);
        setResult(null);

        try {
            const response = await axiosNewInstance.post(
                `api/agencyDetails/${encodeURIComponent(codeToUse)}`,
                {
                    agType: "FA",
                    startDt: formatDate(startDt),
                    endDt: formatDate(endDt),
                },
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json",
                    },
                }
            );
            setResult(response?.data ?? null);
        } catch (err) {
            const message =
                err?.response?.data?.Error ||
                err?.response?.data?.message ||
                err?.message ||
                "Unable to fetch agency details.";
            setError(message);
        } finally {
            setLoading(false);
        }
    };

    const renderResult = () => {
        if (!result) return null;

        // Try to find an array of records in the response
        const records =
            result?.agencyDetails ??
            result?.agency_details ??
            result?.data?.agencyDetails ??
            result?.data?.agency_details ??
            result?.policyDetails ??
            result?.policy_details ??
            (Array.isArray(result) ? result : null);

        // If it's an array, render as table
        if (Array.isArray(records) && records.length > 0) {
            const columns = Object.keys(records[0]);

            return (
                <div className="table-responsive mt-3" style={{ height: "500px", overflowY: "auto" }}>
                    <table className="table text-left">
                        <thead>
                            <tr>
                                <th scope="col">#</th>
                                {columns.map((col) => (
                                    <th key={col} scope="col" style={{ textTransform: "none" }}>
                                        {col}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {records.map((row, index) => (
                                <tr key={index}>
                                    <td>{index + 1}</td>
                                    {columns.map((col) => (
                                        <td key={col}>
                                            {typeof row[col] === "object" && row[col] !== null
                                                ? JSON.stringify(row[col])
                                                : String(row[col] ?? "")}
                                        </td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            );
        }

        // If it's an object (single record), show key-value pairs
        if (typeof result === "object" && result !== null && !Array.isArray(result)) {
            return (
                <div className="table-responsive mt-3">
                    <table className="table table-sm">
                        <tbody>
                            {Object.entries(result).map(([key, value]) => (
                                <tr key={key}>
                                    <th scope="row" style={{ width: "30%", textTransform: "none" }}>
                                        {key}
                                    </th>
                                    <td>
                                        {typeof value === "object" && value !== null
                                            ? JSON.stringify(value, null, 2)
                                            : String(value ?? "")}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            );
        }

        // Fallback: raw JSON
        return (
            <div className="table-responsive mt-3">
                <pre className="p-3 bg-light rounded small mb-0 mt-2">
                    {JSON.stringify(result, null, 2)}
                </pre>
            </div>
        );
    };

    return (
        <div className="container">
            <div className="row g-0">
                <div className="col-md-12 pr-2 mt-4">
                    <div className="card p-3 shadow-sm">
                        <h5 className="font-weight-bold fs-4 my-3">
                            <LangData keyname="agency_details" />
                        </h5>

                        {/* Agent Info Card — only for Agent role */}
                        {isAgent && agentInfo && (
                            <div className="card mb-3" style={{ background: "#f0f7ff", border: "1px solid #b3d4fc" }}>
                                <div className="card-body py-2 px-3">
                                    <div className="row">
                                        <div className="col-md-4 mb-1">
                                            <small className="text-muted d-block">Agent Name</small>
                                            <strong>{agentInfo.agentName || "N/A"}</strong>
                                        </div>
                                        <div className="col-md-2 mb-1">
                                            <small className="text-muted d-block">Agency Code</small>
                                            <strong>{agentInfo.agencyCode || "N/A"}</strong>
                                        </div>
                                        <div className="col-md-2 mb-1">
                                            <small className="text-muted d-block">Agent Type</small>
                                            <strong>{agentInfo.agentType || "N/A"}</strong>
                                        </div>
                                        <div className="col-md-2 mb-1">
                                            <small className="text-muted d-block">Email</small>
                                            <strong>{agentInfo.agentEmail || "N/A"}</strong>
                                        </div>
                                        <div className="col-md-2 mb-1">
                                            <small className="text-muted d-block">Mobile</small>
                                            <strong>{agentInfo.agentMobileNo || "N/A"}</strong>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Loading spinner for Agents */}
                        {isAgent && agencyLoading && (
                            <p className="text-muted mb-0">
                                <span className="spinner-border spinner-border-sm mr-1" />
                                Loading agent info...
                            </p>
                        )}

                        {/* Search filters — Agent: date only, LIC Employee: agent code + dates */}
                        {(isLicEmployee || (!agencyLoading && agencyCode)) && (
                            <div className="row mb-3 align-items-end">
                                {/* Agent Code input — only for LIC Employee */}
                                {isLicEmployee && (
                                    <div className="col-md-3">
                                        <label className="font-weight-bold mb-1">Agent Code</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            placeholder="Enter Agent Code"
                                            value={manualAgentCode}
                                            onChange={(e) => setManualAgentCode(e.target.value)}
                                        />
                                    </div>
                                )}
                                <div className="col-md-3">
                                    <label className="font-weight-bold mb-1">Start Date</label>
                                    <DatePicker
                                        selected={startDt}
                                        onChange={(date) => setStartDt(date)}
                                        dateFormat="yyyy-MM-dd"
                                        className="form-control"
                                        placeholderText="Start Date"
                                    />
                                </div>
                                <div className="col-md-3">
                                    <label className="font-weight-bold mb-1">End Date</label>
                                    <DatePicker
                                        selected={endDt}
                                        onChange={(date) => setEndDt(date)}
                                        dateFormat="yyyy-MM-dd"
                                        className="form-control"
                                        placeholderText="End Date"
                                    />
                                </div>
                                <div className="col-md-3">
                                    <button
                                        type="button"
                                        className="btn btn-primary"
                                        onClick={fetchAgencyDetails}
                                        disabled={loading}
                                    >
                                        {loading ? (
                                            <>
                                                <span className="spinner-border spinner-border-sm mr-1" />
                                                Searching...
                                            </>
                                        ) : (
                                            "Search"
                                        )}
                                    </button>
                                </div>
                            </div>
                        )}

                        {error && (
                            <div className="alert alert-danger py-2" role="alert">
                                {error}
                            </div>
                        )}
                        {!loading && result && renderResult()}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AgencyDetails;
